import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Input } from './Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import {
  parseN8nWorkflowJson,
  convertN8nWorkflow,
  getN8nWorkflowStats,
  fetchN8nWorkflowFromUrl,
  N8nWorkflow,
} from '../helpers/n8nWorkflowConverter';
import { WorkflowDefinition } from '../helpers/workflowTypes';
import {
  Upload,
  Link,
  FileJson,
  AlertCircle,
  CheckCircle,
  Loader2,
  GitBranch,
  Code,
  Webhook,
  Sparkles,
  Database,
  MessageSquare,
} from 'lucide-react';
import styles from './ImportN8nWorkflowDialog.module.css';

interface ImportN8nWorkflowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (workflow: WorkflowDefinition) => void;
}

type ImportMethod = 'paste' | 'url' | 'file';

const nodeTypeIcons: Record<string, React.ReactNode> = {
  trigger_webhook: <Webhook size={14} />,
  trigger_schedule: <Webhook size={14} />,
  trigger_manual: <Webhook size={14} />,
  action_http: <Code size={14} />,
  action_code: <Code size={14} />,
  action_ai: <Sparkles size={14} />,
  action_slack: <MessageSquare size={14} />,
  action_discord: <MessageSquare size={14} />,
  action_email: <MessageSquare size={14} />,
  action_database: <Database size={14} />,
  logic_if: <GitBranch size={14} />,
  logic_switch: <GitBranch size={14} />,
  logic_loop: <GitBranch size={14} />,
  logic_delay: <GitBranch size={14} />,
  logic_parallel: <GitBranch size={14} />,
};

export const ImportN8nWorkflowDialog: React.FC<ImportN8nWorkflowDialogProps> = ({
  open,
  onOpenChange,
  onImport,
}) => {
  const [importMethod, setImportMethod] = useState<ImportMethod>('paste');
  const [jsonInput, setJsonInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    workflow: N8nWorkflow;
    stats: ReturnType<typeof getN8nWorkflowStats>;
  } | null>(null);

  const resetState = useCallback(() => {
    setJsonInput('');
    setUrlInput('');
    setError(null);
    setPreview(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [resetState, onOpenChange]);

  const handlePreview = useCallback(async () => {
    setError(null);
    setPreview(null);
    setLoading(true);

    try {
      let workflow: N8nWorkflow;

      if (importMethod === 'paste') {
        if (!jsonInput.trim()) {
          throw new Error('Please paste the n8n workflow JSON');
        }
        workflow = parseN8nWorkflowJson(jsonInput);
      } else if (importMethod === 'url') {
        if (!urlInput.trim()) {
          throw new Error('Please enter a URL');
        }
        workflow = await fetchN8nWorkflowFromUrl(urlInput);
      } else {
        throw new Error('Please select a file');
      }

      const stats = getN8nWorkflowStats(workflow);
      setPreview({ workflow, stats });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse workflow');
    } finally {
      setLoading(false);
    }
  }, [importMethod, jsonInput, urlInput]);

  const handleImport = useCallback(() => {
    if (!preview) return;

    try {
      const converted = convertN8nWorkflow(preview.workflow);
      onImport(converted);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert workflow');
    }
  }, [preview, onImport, handleClose]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      setImportMethod('paste');
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialog}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <FileJson size={20} />
            Import n8n Workflow
          </DialogTitle>
          <DialogDescription>
            Import workflow from n8n by pasting JSON, providing a URL, or uploading a file.
          </DialogDescription>
        </DialogHeader>

        <div className={styles.content}>
          <Tabs
            value={importMethod}
            onValueChange={(v) => {
              setImportMethod(v as ImportMethod);
              setError(null);
              setPreview(null);
            }}
          >
            <TabsList className={styles.tabsList}>
              <TabsTrigger value="paste" className={styles.tabTrigger}>
                <FileJson size={14} />
                Paste JSON
              </TabsTrigger>
              <TabsTrigger value="url" className={styles.tabTrigger}>
                <Link size={14} />
                From URL
              </TabsTrigger>
              <TabsTrigger value="file" className={styles.tabTrigger}>
                <Upload size={14} />
                Upload File
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className={styles.tabContent}>
              <Textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='Paste your n8n workflow JSON here...&#10;&#10;{&#10;  "name": "My Workflow",&#10;  "nodes": [...],&#10;  "connections": {...}&#10;}'
                className={styles.textarea}
              />
            </TabsContent>

            <TabsContent value="url" className={styles.tabContent}>
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://raw.githubusercontent.com/.../workflow.json"
                className={styles.urlInput}
              />
              <p className={styles.hint}>
                Enter a direct URL to an n8n workflow JSON file (e.g., raw GitHub URL)
              </p>
            </TabsContent>

            <TabsContent value="file" className={styles.tabContent}>
              <label className={styles.fileUpload}>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                />
                <Upload size={24} />
                <span>Click to upload or drag and drop</span>
                <span className={styles.fileHint}>.json files only</span>
              </label>
            </TabsContent>
          </Tabs>

          {error && (
            <div className={styles.error}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {preview && (
            <div className={styles.preview}>
              <div className={styles.previewHeader}>
                <CheckCircle size={16} className={styles.successIcon} />
                <span>Workflow ready to import</span>
              </div>

              <div className={styles.previewDetails}>
                <div className={styles.previewName}>{preview.workflow.name}</div>

                {preview.workflow.description && (
                  <div className={styles.previewDescription}>{preview.workflow.description}</div>
                )}

                <div className={styles.previewStats}>
                  <div className={styles.stat}>
                    <span className={styles.statValue}>{preview.stats.nodeCount}</span>
                    <span className={styles.statLabel}>Nodes</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statValue}>{preview.stats.connectionCount}</span>
                    <span className={styles.statLabel}>Connections</span>
                  </div>
                  <div className={styles.stat}>
                    <span
                      className={`${styles.statValue} ${preview.stats.hasTrigger ? styles.success : styles.warning}`}
                    >
                      {preview.stats.hasTrigger ? 'Yes' : 'No'}
                    </span>
                    <span className={styles.statLabel}>Has Trigger</span>
                  </div>
                </div>

                <div className={styles.nodeTypes}>
                  <span className={styles.nodeTypesLabel}>Node types:</span>
                  <div className={styles.nodeTypesList}>
                    {Object.entries(preview.stats.nodeTypes).map(([type, count]) => (
                      <div key={type} className={styles.nodeType}>
                        {nodeTypeIcons[type] || <Code size={14} />}
                        <span>{type.replace('_', ' ')}</span>
                        <span className={styles.nodeTypeCount}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className={styles.footer}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>

          {!preview ? (
            <Button onClick={handlePreview} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  Parsing...
                </>
              ) : (
                'Preview'
              )}
            </Button>
          ) : (
            <Button onClick={handleImport}>Import Workflow</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
